import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CommonModal from "../../../components/common/CommonModal";

import { ThemeProvider } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import CssBaseline from "@mui/material/CssBaseline";
import { createRoot } from "react-dom/client";
import jsPDF from "jspdf";
import { addIdCardCanvasToPdf, captureIdCardElement, ID_CARD_EXPORT_WIDTH_PX } from "utils/idCardExport";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { useAuth } from "../../../contexts/AuthContext";
import { AuthProvider } from "../../../contexts/AuthContext";
import { useError } from "../../../contexts/ErrorContext";
import { useSessionClass } from "../../../contexts/SessionClassContext";
import { useClasses } from "../../../hooks/useClasses";
import { useSchools } from "../../../hooks/useSchools";
import { useSessions } from "../../../hooks/useSession";
import { useStudents } from "../../../hooks/useStudents";
import { useSchoolIdForAdmin } from "../../../hooks/useSchoolIdForAdmin";

import { theme } from "../../../theme";
import IdCardTemplate from "../../../components/templates/IdCardTemplate";
import IdCardFormModal from "../../../components/Modals/IdCardFormModal";

const bulkQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 10 * 60 * 1000,
      retry: 1,
    },
  },
});

type IdCardPreviewData = {
  studentId?: number;
  schoolId?: number;
  logoFileName?: string | null;
  skipSchoolLogoFetch?: boolean;
  photoUrl?: string | null;
  skipStudentMediaFetch?: boolean;
  schoolName: string;
  district?: string;
  schoolTagline: string;
  schoolAddressLines: string[];
  phoneNo: string;
  studentName: string;
  fatherName: string;
  motherName: string;
  dob: string;
  contactNo: string;
  mobileNo?: string;
  address: string;
  className: string;
  section?: string;
};

const formatDob = (dob: string | number[] | null | undefined) => {
  if (!dob) return "";
  if (Array.isArray(dob) && dob.length >= 3) {
    const [year, month, day] = dob;
    return `${String(day).padStart(2, "0")}-${String(month).padStart(2, "0")}-${String(year)}`;
  }
  if (typeof dob === "string") {
    const ymd = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) return `${ymd[3]}-${ymd[2]}-${ymd[1]}`;
    return dob;
  }
  return "";
};

const buildAddress = (student: any) => {
  const parts: string[] = [];
  if (student.houseNo) parts.push(`${student.houseNo}`);
  if (student.city) parts.push(student.city);
  return parts.join(", ");
};

const IdCardManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user, isSchoolAdmin } = useAuth();
  const { showError, showSuccess } = useError();

  const isStudent = user?.roles?.includes("STUDENT") ?? false;

  const { selectedClass: layoutSelectedClass, selectedSession: layoutSelectedSession } = useSessionClass();
  const layoutSelectedClassId =
    layoutSelectedClass && layoutSelectedClass.id != null ? Number(layoutSelectedClass.id) : undefined;
  const layoutSelectedSessionId =
    layoutSelectedSession && layoutSelectedSession.id != null ? Number(layoutSelectedSession.id) : undefined;

  const [modalOpen, setModalOpen] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);

  const [bulkSelectedClass, setBulkSelectedClass] = useState<number | string | ''>('');
  const [bulkSelectedSession, setBulkSelectedSession] = useState<number | ''>('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [currentStudent, setCurrentStudent] = useState("");

  const isBulkDownloadCancelledRef = useRef(false);
  const [bulkSelectedStudentIds, setBulkSelectedStudentIds] = useState<Set<number>>(new Set());

  const shouldFetchAdminSchoolId = !!user && isSchoolAdmin() && !user.schoolId;
  const adminSchoolIdQuery = useSchoolIdForAdmin(shouldFetchAdminSchoolId ? user?.id : undefined);
  const effectiveSchoolId = user?.schoolId ?? adminSchoolIdQuery.data ?? undefined;

  const { useSchoolById, getSchoolLogoInfo } = useSchools(false, { disableSchoolAdmins: true });
  const { data: schoolData } = useSchoolById(effectiveSchoolId);

  const {
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
    getStudentByStudentCode,
    getStudentsByStudentCodesBulk,
    getStudentPhotoInfosBulk,
  } = useStudents(false, effectiveSchoolId);
  const { classesQuery } = useClasses(effectiveSchoolId);
  const { sessionsQuery } = useSessions(effectiveSchoolId);

  const normalizedBulkClassId = typeof bulkSelectedClass === "number" ? bulkSelectedClass : undefined;
  const normalizedBulkClassName = typeof bulkSelectedClass === "string" && bulkSelectedClass.trim() ? bulkSelectedClass : undefined;
  const normalizedBulkSession = typeof bulkSelectedSession === "number" ? bulkSelectedSession : undefined;

  const studentsByIdQuery = useStudentsBySessionIdAndClassId(normalizedBulkSession, normalizedBulkClassId);
  const studentsByNameQuery = useStudentsBySessionIdAndClassName(normalizedBulkSession, normalizedBulkClassName);

  const studentsQuery = normalizedBulkClassName ? studentsByNameQuery : studentsByIdQuery;
  const { data: students = [], isLoading: studentsLoading } = studentsQuery;

  const bulkVisibleStudents = useMemo(() => {
    return students
      .filter((s: any) => s.status === true && typeof s.id === "number")
      .slice()
      .sort((a: any, b: any) =>
        String(a.studentName || "").localeCompare(String(b.studentName || ""))
      );
  }, [students]);

  useEffect(() => {
    setBulkSelectedStudentIds(
      new Set(bulkVisibleStudents.map((s: any) => s.id as number))
    );
  }, [normalizedBulkSession, normalizedBulkClassId, normalizedBulkClassName, bulkVisibleStudents]);

  const bulkSelectAllChecked =
    bulkVisibleStudents.length > 0 &&
    bulkVisibleStudents.every((s: any) => bulkSelectedStudentIds.has(s.id));
  const bulkSelectAllIndeterminate =
    bulkSelectedStudentIds.size > 0 && !bulkSelectAllChecked;

  const mapStudentToIdCardData = useCallback(
    (student: any): IdCardPreviewData => {
      const schoolName = schoolData?.nameofSchool || "School Name";
      const district = schoolData?.district || "District";
      const state = schoolData?.state || "State";
      const pinNo = schoolData?.pinNo || "000000";
      const principalMobile = schoolData?.principalMobile || "";

      const mobile = student.mobileNo || "";
      return {
        studentId: student?.id,
        schoolId: effectiveSchoolId ? Number(effectiveSchoolId) : undefined,
        schoolName,
        district: schoolData?.district || "",
        schoolTagline: "(Govt. Recognised)",
        schoolAddressLines: ["Place your address,", `${district} ${state} and Pin - ${pinNo}`],
        phoneNo: String(principalMobile),
        studentName: student.studentName || "",
        fatherName: student.fatherName || "",
        motherName: student.motherName || "",
        dob: formatDob(student.dateOfBirth),
        contactNo: mobile,
        mobileNo: mobile,
        address: buildAddress(student),
        className: student.className || "",
        section: (student as any)?.section || student.section || "",
      };
    },
    [effectiveSchoolId, schoolData]
  );

  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  const handleOpenBulkModal = () => {
    if (layoutSelectedSessionId) setBulkSelectedSession(layoutSelectedSessionId);
    if (layoutSelectedClass) {
      if ((layoutSelectedClass as any)?.section === "__CLASS_ALL__") {
        setBulkSelectedClass("Class-ALL");
      } else if ((layoutSelectedClass as any)?.section === "__GRADE_ALL__") {
        setBulkSelectedClass(`${String((layoutSelectedClass as any)?.name || "").trim()}-ALL`);
      } else if (layoutSelectedClassId) {
        setBulkSelectedClass(layoutSelectedClassId);
      }
    } else if (layoutSelectedClassId) {
      setBulkSelectedClass(layoutSelectedClassId);
    }
    setBulkModalOpen(true);
    setError("");
    setIsGenerating(false);
    setProgress(0);
    setCurrentStudent("");
    isBulkDownloadCancelledRef.current = false;
  };

  const handleCloseBulkModal = () => {
    if (isGenerating) return;
    setBulkModalOpen(false);
    setBulkSelectedClass("");
    setBulkSelectedSession("");
    setError("");
    setProgress(0);
    setCurrentStudent("");
    setBulkSelectedStudentIds(new Set());
    isBulkDownloadCancelledRef.current = false;
  };

  const handleCancelBulkDownload = () => {
    isBulkDownloadCancelledRef.current = true;
    setIsGenerating(false);
    setProgress(0);
    setCurrentStudent("");
    showError(t("bulk_download_cancelled") || "Bulk download cancelled.");
  };

  const waitNextFrame = () =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => setTimeout(resolve, 0));
    });

  const waitForFonts = async () => {
    try {
      // @ts-ignore
      if (document.fonts?.ready) await document.fonts.ready;
    } catch {
      // ignore
    }
  };

  /**
   * Wait for async images (school logo, student photo blob) so html2canvas matches the modal.
   * With a student id, wait at least ~2.2s before finishing so the photo effect can add its <img> after the logo.
   */
  const waitForImages = async (container: HTMLElement, studentId?: number, timeoutMs: number = 12000) => {
    const start = Date.now();
    const getImgs = () => Array.from(container.querySelectorAll("img")) as HTMLImageElement[];

    await new Promise((r) => setTimeout(r, 400));

    const sid = studentId != null ? Number(studentId) : NaN;
    const minElapsed = Number.isFinite(sid) ? 2200 : 800;

    while (Date.now() - start < timeoutMs) {
      const elapsed = Date.now() - start;
      const imgs = getImgs();

      if (imgs.length === 0) {
        if (elapsed > 4500) return;
        await new Promise((r) => setTimeout(r, 150));
        continue;
      }

      const allDone = imgs.every((img) => img.complete);
      if (allDone && elapsed >= minElapsed) return;

      await new Promise((r) => setTimeout(r, 150));
    }
  };

  const renderCardOnce = async (data: IdCardPreviewData) => {
    const host = document.createElement("div");
    Object.assign(host.style, {
      position: "fixed",
      left: "-10000px",
      top: "0",
      width: `${ID_CARD_EXPORT_WIDTH_PX}px`,
      height: "auto",
      background: "#ffffff",
      margin: "0",
      padding: "0",
      boxSizing: "border-box",
      overflow: "visible",
    } as CSSStyleDeclaration);
    document.body.appendChild(host);

    const root = createRoot(host);
    root.render(
      <QueryClientProvider client={bulkQueryClient}>
        <AuthProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <IdCardTemplate data={data as any} />
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    );

    await waitNextFrame();
    await waitForFonts();
    await waitNextFrame();
    await waitForImages(host, data.studentId);

    return { host, root };
  };

  const removeHost = (host: HTMLDivElement, root?: ReturnType<typeof createRoot>) => {
    try {
      root?.unmount();
    } catch {
      // ignore
    }
    host.remove();
  };

  const handleBulkDownload = async () => {
    setError("");
    setProgress(0);
    setCurrentStudent("");

    if ((!normalizedBulkClassId && !normalizedBulkClassName) || !normalizedBulkSession) {
      setError(t("please_select_both_class_session_bulk_download") || "Please select both class and session for bulk download");
      return;
    }

    if (bulkVisibleStudents.length === 0) {
      const msg =
        t("no_active_students_found_selected_class_session") || "No active students found for the selected class and session";
      setError(msg);
      showError(msg);
      return;
    }

    const activeStudents = bulkVisibleStudents.filter((s: any) =>
      bulkSelectedStudentIds.has(s.id)
    );

    if (activeStudents.length === 0) {
      const msg =
        (t("please_select_students_to_download") as string) ||
        "Please select at least one student to download.";
      setError(msg);
      showError(msg);
      return;
    }

    setIsGenerating(true);
    isBulkDownloadCancelledRef.current = false;

    try {
      const classInfo = normalizedBulkClassId
        ? (classesQuery.data || []).find((c: any) => c.id === normalizedBulkClassId)
        : null;
      const sessionInfo = (sessionsQuery.data || []).find((s: any) => s.id === normalizedBulkSession);
      const className =
        normalizedBulkClassName ||
        (classInfo?.section ? `${classInfo.name} ${classInfo.section}` : classInfo?.name) ||
        "Class";
      const sessionName = sessionInfo?.name || "Session";

      const filename = `bulk-id-cards-${className}-${sessionName}-${activeStudents.length}-students.pdf`;

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
      });

      // Prefetch school logo info once (avoid N× /schools/{id}/logo-info)
      let bulkLogoFileName: string | null = null;
      try {
        const sid = effectiveSchoolId != null ? Number(effectiveSchoolId) : NaN;
        if (Number.isFinite(sid)) {
          const logoInfo = await getSchoolLogoInfo(sid);
          bulkLogoFileName = (logoInfo as any)?.fileName || null;
        }
      } catch {
        bulkLogoFileName = null;
      }

      // Prefetch: bulk student details once (avoid N× /students/studentCode)
      const uniqueCodes = Array.from(
        new Set(
          activeStudents
            .map((s: any) => s?.studentCode)
            .filter((c: any) => typeof c === "string" && c.trim().length > 0)
        )
      );
      const bulkStudentsByCode = new Map<string, any>();
      try {
        if (uniqueCodes.length > 0) {
          const bulkStudents = await getStudentsByStudentCodesBulk(uniqueCodes);
          (bulkStudents || []).forEach((st: any) => {
            if (st?.studentCode) bulkStudentsByCode.set(String(st.studentCode), st);
          });
        }
      } catch {
        // fallback: per-student fetch (existing behavior)
      }

      // Prefetch: bulk photo info once (avoid N× /students/{id}/studentPhoto)
      const bulkPhotoInfoById = new Map<number, { fileName?: string }>();
      try {
        const idsToFetch = Array.from(
          new Set(
            (Array.from(bulkStudentsByCode.values()) as any[])
              .map((s) => s?.id)
              .filter((id) => typeof id === "number" && !Number.isNaN(id))
          )
        ) as number[];
        if (idsToFetch.length > 0) {
          const photoInfos = await getStudentPhotoInfosBulk(idsToFetch);
          (photoInfos || []).forEach((pi: any) => {
            const sid = Number(pi?.studentId);
            if (!Number.isNaN(sid)) {
              bulkPhotoInfoById.set(sid, { fileName: pi?.fileName || "" });
            }
          });
        }
      } catch {
        // fallback: template will fetch per student photo (existing behavior)
      }

      const buildImageHeaders = (): Record<string, string> => {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        const tenantSchema = localStorage.getItem("tenantSchema") || sessionStorage.getItem("tenantSchema");
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        if (tenantSchema) headers["X-Tenant-ID"] = tenantSchema;
        return headers;
      };

      const fetchBlobUrlForFileName = async (fileName?: string): Promise<string | null> => {
        if (!fileName || typeof fileName !== "string" || fileName.trim().length === 0) return null;
        const base = process.env.REACT_APP_API_URL || "http://localhost:8080/schoolErp";
        const url = `${base}/students/photo/${fileName}`;
        try {
          const res = await fetch(url, { headers: buildImageHeaders() });
          if (!res.ok) return null;
          const blob = await res.blob();
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      };

      for (let i = 0; i < activeStudents.length; i++) {
        if (isBulkDownloadCancelledRef.current) break;

        const student = activeStudents[i];
        setCurrentStudent(student.studentName || "");

        let fullStudent = student;
        try {
          const code = student?.studentCode ? String(student.studentCode) : "";
          if (code && bulkStudentsByCode.has(code)) {
            fullStudent = bulkStudentsByCode.get(code);
          } else if (code) {
            fullStudent = await getStudentByStudentCode(code);
          }
        } catch {
          // fallback to list student
        }

        const baseTemplateData = mapStudentToIdCardData(fullStudent);
        const sid = baseTemplateData.studentId != null ? Number(baseTemplateData.studentId) : NaN;
        const skipStudentMediaFetch = Number.isFinite(sid) ? bulkPhotoInfoById.has(sid) : false;
        const photoInfo = Number.isFinite(sid) ? bulkPhotoInfoById.get(sid) : undefined;

        let photoObjectUrl: string | null = null;
        try {
          if (photoInfo?.fileName) {
            photoObjectUrl = await fetchBlobUrlForFileName(photoInfo.fileName);
          }
        } catch {
          photoObjectUrl = null;
        }

        const templateData: IdCardPreviewData = {
          ...baseTemplateData,
          logoFileName: bulkLogoFileName,
          skipSchoolLogoFetch: true,
          photoUrl: photoObjectUrl,
          skipStudentMediaFetch,
        };

        try {
          const { host, root } = await renderCardOnce(templateData);

          const canvas = await captureIdCardElement(host);
          // Use PNG to avoid JPEG compression artifacts that change thin wave lines.
          const imgData = canvas.toDataURL("image/png");

          if (i > 0) pdf.addPage();
          addIdCardCanvasToPdf(pdf, canvas, imgData, "PNG");

          removeHost(host, root);
        } finally {
          if (photoObjectUrl) URL.revokeObjectURL(photoObjectUrl);
        }

        setProgress(Math.round(((i + 1) / activeStudents.length) * 100));
      }

      if (!isBulkDownloadCancelledRef.current) {
        pdf.save(filename);
        showSuccess(
          t("successfully_generated_id_cards", { count: activeStudents.length }) ||
            `Successfully generated ${activeStudents.length} ID cards.`
        );
      }
    } catch (e: any) {
      setError(e?.message || "Failed to generate bulk ID cards.");
      showError(e?.message || "Failed to generate bulk ID cards.");
    } finally {
      setIsGenerating(false);
      setProgress(0);
      setCurrentStudent("");
      isBulkDownloadCancelledRef.current = false;
    }
  };

  const activeStudents = useMemo(() => students.filter((s: any) => s.status === true), [students]);
  const activeStudentsCount = activeStudents.length;

  return (
    <>
      <Paper sx={{ p: 1.5, maxWidth: 1500, mx: "auto", mt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              fontSize: "1.25rem",
              whiteSpace: "nowrap",
            }}
          >
            {t("id_card_management")}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            {!isStudent && (
              <Button
                variant="contained"
                size="small"
                sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
                onClick={handleOpenBulkModal}
              >
                {t("bulk_download_id_cards") || "Bulk Download ID Cards"}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, height: 36 }}
              onClick={handleOpenModal}
            >
              {t("get_id_card") || `Get ${t("id_card")}`}
            </Button>
          </Box>
        </Box>

        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t("id_card_generator_title") || "ID Card Generator"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t("id_card_generator_subtitle") || "Generate and download a single ID card or bulk ID cards as PDF."}
          </Typography>
        </Box>
      </Paper>

      <IdCardFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        selectedClassId={layoutSelectedClassId}
        selectedSessionId={layoutSelectedSessionId}
      />

      {/* Bulk Download Modal */}
      {!isStudent && (
        <CommonModal
          open={bulkModalOpen}
          onClose={handleCloseBulkModal}
          title={t("bulk_download_id_cards") || "Bulk Download ID Cards"}
          maxWidth="md"
          customActions={
            <>
              <Button
                onClick={isGenerating ? handleCancelBulkDownload : handleCloseBulkModal}
                variant="outlined"
                sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
              >
                {t("cancel") || "Cancel"}
              </Button>
              <LoadingButton
                onClick={handleBulkDownload}
                variant="contained"
                loading={isGenerating}
                loadingPosition="start"
                sx={{
                  minWidth: "200px",
                  backgroundColor: "#3092e3",
                  "&:hover": { backgroundColor: "primary.main" },
                  "&.Mui-disabled": { backgroundColor: "#b2ebff" },
                }}
                disabled={
                  (!normalizedBulkClassId && !normalizedBulkClassName) ||
                  !normalizedBulkSession ||
                  isGenerating ||
                  studentsLoading ||
                  activeStudentsCount === 0 ||
                  bulkSelectedStudentIds.size === 0
                }
              >
                {t("generate_pdf") || "Generate PDF"}
              </LoadingButton>
            </>
          }
        >
          <Box sx={{ py: 2 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t("select_class_session_to_download_id_cards") || "Select Class & Session to download ID cards."}
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t("session") || "Session"}</InputLabel>
                <Select
                  value={bulkSelectedSession}
                  label={t("session") || "Session"}
                  onChange={(e) => setBulkSelectedSession(e.target.value === "" ? "" : Number(e.target.value))}
                  disabled={isGenerating || sessionsQuery.isLoading}
                >
                  {(sessionsQuery.data || []).map((session: any) => (
                    <MenuItem key={session.id} value={session.id}>
                      {session.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t("class") || "Class"}</InputLabel>
                <Select
                  value={bulkSelectedClass}
                  label={t("class") || "Class"}
                  onChange={(e) => {
                    const v = e.target.value as any;
                    if (v === "") return setBulkSelectedClass("");
                    // Allow either numeric ids or synthetic "*-ALL" strings.
                    const asNumber = Number(v);
                    setBulkSelectedClass(Number.isFinite(asNumber) && String(v) === String(asNumber) ? asNumber : String(v));
                  }}
                  disabled={isGenerating || classesQuery.isLoading || !normalizedBulkSession}
                >
                  {(() => {
                    const base = (classesQuery.data || []).filter((cls: any) => {
                      if (!normalizedBulkSession) return true;
                      const classSessionId = cls.sessionId ?? cls.session?.id ?? cls.sessionId?.id;
                      if (classSessionId === undefined || classSessionId === null) return true;
                      return Number(classSessionId) === Number(normalizedBulkSession);
                    });

                    const gradeNames = Array.from(new Set(base.map((c: any) => c?.name).filter(Boolean))) as string[];
                    const getClassSortValue = (className?: string) => {
                      if (!className) return Number.MAX_SAFE_INTEGER;
                      const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
                      if (normalized.includes("nursery")) return -3;
                      if (normalized.includes("lkg")) return -2;
                      if (normalized.includes("ukg")) return -1;
                      const m = className.match(/\d+/);
                      return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
                    };
                    const getSectionSortValue = (section?: string | null) => {
                      if (!section) return 0;
                      const s = String(section).trim().toUpperCase();
                      if (!s) return 0;
                      const code = s.charCodeAt(0);
                      return code >= 65 && code <= 90 ? code - 64 : Number.MAX_SAFE_INTEGER;
                    };

                    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));
                    const out: any[] = [];
                    // Global ALL
                    out.push(
                      <MenuItem key="__class_all__" value="Class-ALL">
                        Class-ALL
                      </MenuItem>
                    );
                    for (const grade of gradeNames) {
                      out.push(
                        <MenuItem key={`${grade}-ALL`} value={`${grade}-ALL`}>
                          {grade}-ALL
                        </MenuItem>
                      );
                      base
                        .filter((c: any) => c?.name === grade)
                        .sort((a: any, b: any) => {
                          const cd = getClassSortValue(a?.name) - getClassSortValue(b?.name);
                          if (cd !== 0) return cd;
                          const sd = getSectionSortValue(a?.section) - getSectionSortValue(b?.section);
                          if (sd !== 0) return sd;
                          return (a?.name || "").localeCompare(b?.name || "");
                        })
                        .forEach((cls: any) => {
                          out.push(
                            <MenuItem key={cls.id} value={cls.id}>
                              {cls.section ? `${cls.name} ${cls.section}` : cls.name}
                            </MenuItem>
                          );
                        });
                    }
                    return out;
                  })()}
                </Select>
              </FormControl>

              {(normalizedBulkClassId || normalizedBulkClassName) && normalizedBulkSession && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: "info.light", borderRadius: 1 }}>
                  <Typography variant="body2" color="info.contrastText">
                    {t("active_students_found", { count: activeStudentsCount }) ||
                      `Active students found: ${activeStudentsCount}`}
                  </Typography>
                </Box>
              )}

              {bulkVisibleStudents.length > 0 && (
                <TableContainer component={Paper} sx={{ overflow: "hidden", mb: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" sx={{ backgroundColor: "background.paper", py: 0.8 }}>
                          <Checkbox
                            checked={bulkSelectAllChecked}
                            indeterminate={bulkSelectAllIndeterminate}
                            disabled={isGenerating}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBulkSelectedStudentIds(
                                  new Set(bulkVisibleStudents.map((s: any) => s.id as number))
                                );
                              } else {
                                setBulkSelectedStudentIds(new Set());
                              }
                            }}
                            inputProps={{
                              "aria-label": (t("select_all") as string) || "Select All",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "background.paper", py: 0.8, fontSize: "0.95rem" }}>
                          {(t("select_all") as string) || "Select All"} / {t("student_name") || "Student name"}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkVisibleStudents.map((student: any) => (
                        <TableRow key={student.id} hover>
                          <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                            <Checkbox
                              checked={bulkSelectedStudentIds.has(student.id)}
                              disabled={isGenerating}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setBulkSelectedStudentIds((prev) => {
                                  const next = new Set(prev);
                                  if (checked) next.add(student.id);
                                  else next.delete(student.id);
                                  return next;
                                });
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5, fontSize: "0.95rem" }}>{student.studentName || ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {isGenerating && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">{`${t("generating_pdf") || "Generating PDF..."} ${progress}%`}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={progress} />
                  {currentStudent && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {t("processing", { studentName: currentStudent }) || `Processing: ${currentStudent}`}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
        </CommonModal>
      )}
    </>
  );
};

export default IdCardManagement;

